import EditIcon from '@mui/icons-material/Edit'
import { Alert, IconButton } from '@mui/material'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import Spin from 'pared/components/basicUi/spin'
import Table from 'pared/components/basicUi/table'
import COLORS from 'pared/constants/colors'
import { MOBILE_WIDTH } from 'pared/constants/styles'

import { PREP_ITEM_FLOORS } from '../prepItemFloors'
import EditPrepSettingsDefaultModal from './components/EditPrepSettingsDefaultModal'
import EditPrepSettingsScheduleModal from './components/EditPrepSettingsScheduleModal'
import usePrepSettingScopes from './hooks/usePrepSettingScopes'
import usePrepSettings from './hooks/usePrepSettings'
import usePrepSettingsScheduleColumns from './hooks/usePrepSettingsScheduleColumns'
import formatRuleSummary from './ruleSummary'

const Container = styled.div`
  margin-bottom: 40px;
  width: 100%;
  min-width: 800px;
  max-width: 1200px;

  @media ${MOBILE_WIDTH} {
    width: 100%;
    min-width: 400px;
    padding: 0 25px;
  }
`

const Title = styled.h2`
  margin: 0 0 25px 0;
  font-family: Lexend-SemiBold;
  font-size: 20px;
  font-weight: 600;
  color: ${COLORS.Chalkboard};

  @media ${MOBILE_WIDTH} {
    font-size: 20px;
    margin-bottom: 20px;
  }
`

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Lexend-Regular;
  font-size: 16px;
  color: ${COLORS.Chalkboard};
`

const SettingValue = styled.span`
  font-family: Lexend-SemiBold;
  font-weight: 600;
`

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0 12px 0;
  font-family: Lexend-Medium;
  font-size: 16px;
  font-weight: 500;
  color: ${COLORS.Chalkboard};
`

const rowStyle = `
  &:nth-child(even) {
    background-color: ${COLORS.Porcelain};
  }
`

const SETTING_NAME = 'Prep Amount Increase Percent'

const PrepItemAdminSetting = () => {
  const {
    prepSettings,
    loading: prepSettingsLoading,
    refetch,
  } = usePrepSettings()
  const {
    scopes,
    scopeById,
    loading: scopesLoading,
    error: scopesError,
  } = usePrepSettingScopes()
  const scheduleColumns = usePrepSettingsScheduleColumns(scopeById)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editScheduleModalVisible, setEditScheduleModalVisible] =
    useState(false)

  const prepAmountIncreaseSetting = prepSettings.find(
    (setting) => setting.settingName === SETTING_NAME,
  )

  const settingRules = prepAmountIncreaseSetting?.settingRules ?? []

  const scheduleDataSource = useMemo(() => {
    return (
      prepAmountIncreaseSetting?.settingSchedules.map((schedule, index) => ({
        ...schedule,
        key: index,
      })) ?? []
    )
  }, [prepAmountIncreaseSetting])

  return (
    <>
      <Container>
        <Title>Prep Amount Adjustments</Title>
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>
            {PREP_ITEM_FLOORS.length} items have a guaranteed minimum increase.
          </strong>{' '}
          If a percentage below is lower than the minimum, these items still
          prep at their minimum. Anything above it applies normally.
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {PREP_ITEM_FLOORS.map((item) => (
              <li key={item.id}>
                {item.label} — at least {item.floorPercent}%
              </li>
            ))}
          </ul>
        </Alert>
        <Spin spinning={prepSettingsLoading}>
          <SettingRow>
            Standing increase:{' '}
            <SettingValue>
              {settingRules.length === 0
                ? 'No rules — every location unchanged'
                : formatRuleSummary(settingRules, scopeById)}
            </SettingValue>
            <IconButton
              size="small"
              onClick={() => setEditModalVisible(true)}
              sx={{
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <EditIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </SettingRow>
        </Spin>
        <SectionLabel>
          Scheduled Prep Amount Changes
          <IconButton
            size="small"
            onClick={() => setEditScheduleModalVisible(true)}
            sx={{
              padding: '4px',
              marginLeft: '8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <EditIcon sx={{ fontSize: '1.2rem' }} />
          </IconButton>
        </SectionLabel>
        <Spin spinning={prepSettingsLoading}>
          <Table
            columns={scheduleColumns}
            dataSource={scheduleDataSource}
            rowStyle={rowStyle}
            rowKey="startDate"
          />
        </Spin>
      </Container>

      <EditPrepSettingsDefaultModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        settingName={SETTING_NAME}
        currentRules={settingRules}
        scopes={scopes}
        scopesLoading={scopesLoading}
        scopesError={Boolean(scopesError)}
        onSuccess={() => refetch()}
      />

      <EditPrepSettingsScheduleModal
        visible={editScheduleModalVisible}
        onClose={() => setEditScheduleModalVisible(false)}
        settingName={SETTING_NAME}
        currentSchedules={prepAmountIncreaseSetting?.settingSchedules ?? []}
        scopes={scopes}
        scopesLoading={scopesLoading}
        scopesError={Boolean(scopesError)}
        onSuccess={() => refetch()}
      />
    </>
  )
}

export default PrepItemAdminSetting
